import { toApiUrl } from '../../utils/apiBase';
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import firestore from '../../firestore';
import { unsafeAPI, EliteStoreStatus, EliteQualificationType } from '../../hooks/api';
import useFetch from '../../hooks/api';
import { Dialog } from '../../components/Dialog';
import { Power, Plus, Mail, X, Edit } from 'lucide-react';
import { Button } from '../../shadcn/Button';
import { ContainerSpinner } from '../../components/Spinner';
import {
	validate_email,
	validate_bank_account_number,
	validate_identity_number,
	IdentityType,
	validate_phone_number
} from '../../utils/validate';
import { ConfirmDialog, ConfirmDialogProps } from '../../components/ConfirmDialog';
import { ConfirmInputDialog, ConfirmInputDialogProps } from '../../components/ConfirmInputDialog';
import Combobox from '../../components/Combobox';

export default function ManageStores() {
	const { internalCountry, userData, CountrySettings } = useContext(GlobalContext);
	const currency_code = CountrySettings?.currency_code || '';
	const access = userData?.access_type || '';
	const country = internalCountry.get();
	const storeRequestId = useRef(0);

	useEffect(() => {
		setSelectedEmail('');
		setStoreData(undefined);
		setLoading(false);
		return () => {
			storeRequestId.current += 1;
		};
	}, [country]);

	const [loading, setLoading] = useState(false);
	const [dialogMessage, setDialogMessage] = useState('');
	const [selectedEmail, setSelectedEmail] = useState<string>('');
	const [storeSearchOptions, setStoreSearchOptions] = useState<StoreSearchOption[]>([]);
	const [storeData, setStoreData] = useState<StoreData | null | undefined>(undefined);
	const [confirmDialogProps, setConfirmDialogProps] = useState<null | ConfirmDialogProps>(null);
	const [editDialogProps, setEditDialogProps] = useState<null | ConfirmInputDialogProps>(null);
	const banks = CountrySettings?.banks || [];

	// Fetch all store emails on mount
	const { data: storeEmails } = useFetch(
		'https://api.krece.app/storeprofile/masteruser/',
		'GET',
		true,
		undefined,
		undefined,
		true
	);

	useEffect(() => {
		let cancelled = false;
		const fallbackOptions =
			storeEmails?.map((email) => ({
				label: email,
				value: email
			})) || [];

		setStoreSearchOptions(fallbackOptions);

		if (!storeEmails?.length) return;

		const fetchStoreNames = async () => {
			try {
				const storesSnapshot = await firestore.collection('Stores').where('master_user', '==', true).get();
				const namesByEmail = new Map<string, string>();

				storesSnapshot.forEach((doc) => {
					const storeName = doc.data()?.store_name;
					if (typeof storeName === 'string' && storeName.trim()) namesByEmail.set(doc.id, storeName.trim());
				});

				if (cancelled) return;

				setStoreSearchOptions(
					storeEmails.map((email) => {
						const storeName = namesByEmail.get(email);

						return {
							label: storeName || email,
							value: email,
							...(storeName && { description: email, searchText: `${storeName} ${email}` })
						};
					})
				);
			} catch {
				if (!cancelled) setStoreSearchOptions(fallbackOptions);
			}
		};

		fetchStoreNames();

		return () => {
			cancelled = true;
		};
	}, [storeEmails]);

	const handleEmailSelect = (email: string | boolean | null) => {
		if (typeof email !== 'string') {
			storeRequestId.current += 1;
			setLoading(false);
			setSelectedEmail('');
			setStoreData(undefined);
			return;
		}

		setSelectedEmail(email);
		searchStoreDataForEmail(email);
	};

	const searchStoreDataForEmail = async (email: string) => {
		if (!email) {
			setDialogMessage('Por favor seleccione un email de la tienda.');
			return;
		} else if (!validate_email(email)) {
			setDialogMessage('Por favor seleccione un email válido.');
			return;
		} else {
			setDialogMessage('');
		}

		const requestId = ++storeRequestId.current;
		setStoreData(null); // Clear previous store data
		setLoading(true);
		try {
			const response = (await unsafeAPI(
				'POST',
				toApiUrl('get_store_data/'),
				{
					store_email: email?.toLowerCase()?.trim()
				},
				userData,
				internalCountry.get()
			)) as any;

			if (requestId !== storeRequestId.current) return;
			if (response?.success === true && response?.data) {
				// Replace any NaN values with 0 before parsing
				const cleanedData = response.data.replace(/NaN/g, '0');
				const responseData = JSON.parse(cleanedData) as StoreData;
				setStoreData(responseData);
			} else {
				setStoreData(null);
			}
		} catch (err) {
			if (requestId !== storeRequestId.current) return;
			setStoreData(null);
			setDialogMessage((err as Error).message);
		} finally {
			if (requestId === storeRequestId.current) setLoading(false);
		}
	};

	const updateStoreField = async (field_name: string, value: boolean | string | string[] | number): Promise<true | string> => {
		try {
			if (value === null || value === undefined || value === '') return 'Debe rellenar los campos.';

			const response = (await unsafeAPI(
				'POST',
				toApiUrl('update_master_store_field/'),
				{
					store_email: storeData!.store_email?.toLowerCase()?.trim(),
					field_name,
					value
				},
				userData,
				internalCountry.get()
			)) as any;

			if (response?.success === true) {
				setDialogMessage('Actualización exitosa.');
				// Refetch store data to get updated information
				await searchStoreDataForEmail(selectedEmail);
			} else {
				return response?.status?.length ? response.status : 'Ocurrio un problema, intente mas tarde.';
			}
		} catch (err) {
			return 'Error: ' + (err as Error).message;
		}

		return true;
	};

	return (
		<div className="mx-auto w-full max-w-7xl p-4 md:p-[1.25rem]">
			{dialogMessage && (
				<Dialog title={''} onClose={() => setDialogMessage('')}>
					<p>{dialogMessage}</p>
				</Dialog>
			)}

			{confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}

			{editDialogProps && <ConfirmInputDialog {...editDialogProps} />}

			<div className="space-y-4">
				<div className="space-y-0 xl:text-center">
					<h2 className="text-2xl font-semibold text-neutral-100">Administrar Tiendas</h2>
					<p className="text-sm text-neutral-400">Busque por correo o nombre de la tienda master para ver su información</p>
				</div>

				<div className="flex max-w-3xl flex-col gap-4 pt-3 xl:mx-auto">
					<Combobox
						options={storeSearchOptions}
						defaultValue={selectedEmail || undefined}
						onSelectedChange={handleEmailSelect}
						floatingLabel={'Tienda master'}
						placeholder={'Buscar por correo o nombre...'}
						searchPlaceholder={'Buscar por correo o nombre...'}
						emptyMessage="No se encontraron tiendas."
						containerClassName="w-full"
					/>

					{loading && (
						<div className="flex items-center justify-center py-4">
							<ContainerSpinner size={2} />
						</div>
					)}
				</div>
			</div>

			{storeData && (
				<>
					<div className="mt-6 space-y-4">
						<div>
							<h2 className="text-2xl font-semibold text-neutral-100">Información de la tienda</h2>
							<p className="text-sm text-neutral-400">Datos generales</p>
						</div>

						<div className="space-y-2 rounded-lg border border-neutral-800 bg-neutral-900/50 p-3">
							<div className="flex flex-wrap items-center justify-between gap-2">
								<span className="text-sm text-neutral-400">Programa Élite</span>
								<span className="text-sm font-semibold text-neutral-100">{getEliteLabel(storeData.elite_program)}</span>
							</div>
							{storeData.elite_program?.benefits_active && (
								<p className="text-sm text-neutral-300">
									{storeData.elite_program.qualification_type === 'TOP_SALES'
										? 'Califica por volumen de ventas. Beneficios activos.'
										: 'Beneficios activos para la tienda.'}
								</p>
							)}
							{!!storeData.elite_program?.restricted_branches.length && (
								<p className="break-words text-sm text-neutral-300">
									Sucursales sin beneficios Élite:{' '}
									{storeData.elite_program.restricted_branches.map((branch) => branch.branch).join(', ')}
								</p>
							)}
						</div>

						<div className="grid grid-cols-2 gap-3 rounded-lg border border-neutral-800 bg-neutral-900/50 p-3">
							<div className="col-span-2 space-y-3 lg:col-span-1">
								<div className="flex h-9 items-center justify-between rounded-md bg-neutral-800/30 px-3">
									<span className="text-sm text-neutral-400">Nombre de la tienda</span>
									<span className="text-sm text-neutral-100">{storeData.store_name || 'No registrado'}</span>
								</div>

								<div className="flex h-9 items-center justify-between rounded-md bg-neutral-800/30 px-3">
									<span className="text-sm text-neutral-400">Email</span>
									<span className="text-sm text-neutral-100">{storeData.store_email || 'No registrado'}</span>
								</div>

								<div className="flex h-9 items-center justify-between rounded-md bg-neutral-800/30 px-3">
									<span className="text-sm text-neutral-400">Teléfono</span>
									<div className="flex items-center gap-2">
										<span className="text-sm text-neutral-100">{storeData.store_phone || 'No registrado'}</span>
										{['admin'].includes(access) && (
											<Button
												className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
												variant="secondary"
												onClick={() => {
													setEditDialogProps({
														title: 'Editar teléfono de la tienda',
														fields: [
															{
																name: 'store_phone',
																label: 'Teléfono',
																type: 'input',
																placeholder: 'Ingrese el teléfono',
																allowedChars: 'numbers_only',
																defaultValue: storeData.store_phone || ''
															}
														],
														onConfirm: async (values) => {
															const value = String(values?.store_phone || '').trim();
															if (!value) return 'Debe rellenar los campos.';
															if (!validate_phone_number(value, internalCountry.get())) return 'El número de teléfono no es válido.';

															return updateStoreField('store_phone', value);
														},
														onClose: () => setEditDialogProps(null)
													});
												}}
											>
												<span className="flex shrink-0 items-center gap-1">
													<Edit className="size-3.5 shrink-0" />
													<span className="overflow-hidden text-ellipsis whitespace-nowrap"></span>
												</span>
											</Button>
										)}
									</div>
								</div>
							</div>

							<div className="col-span-2 -mt-[0.4rem] space-y-3 lg:col-span-1 lg:-mt-0">
								<div className="flex h-9 items-center justify-between rounded-md bg-neutral-800/30 px-3">
									<span className="text-sm text-neutral-400">Balance</span>
									<span className={`text-sm text-neutral-100`}>
										{storeData.balance !== null ? `${currency_code}${storeData.balance.toFixed(2)}` : 'No disponible'}
									</span>
								</div>

								<div className="flex h-9 items-center justify-between rounded-md bg-neutral-800/30 px-3">
									<span className="text-sm text-neutral-400">Fecha de creación</span>
									<span className="text-sm text-neutral-100">
										{storeData.created_at ? new Date(storeData.created_at).toLocaleDateString() : 'No registrada'}
									</span>
								</div>

								<div className="flex h-9 items-center justify-between rounded-md bg-neutral-800/30 px-3">
									<span className="text-sm text-neutral-400">Sucursales</span>
									<span className="rounded bg-blue-500/10 px-2 py-1 text-xs font-bold text-blue-400 light:brightness-50">
										{storeData.sucursales?.length || 0}
									</span>
								</div>
							</div>
						</div>
					</div>

					<div className="mt-6 space-y-4">
						<div>
							<h2 className="text-2xl font-semibold text-neutral-100">Configuración</h2>
							<p className="text-sm text-neutral-400">Parámetros editables de la tienda</p>
						</div>

						<div className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
							{/* Estado - More prominent */}
							<div className="rounded-lg border border-neutral-700/50 bg-neutral-800/40 p-4">
								<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
									<div className="min-w-0 flex-1">
										<span className="text-base font-medium text-neutral-100">Estado de la tienda</span>
										<p className="mt-1 text-xs text-neutral-400">Control general de activación/desactivación</p>
									</div>
									<div className="flex items-center gap-3 sm:flex-shrink-0">
										<span
											className={`rounded px-3 py-1.5 text-sm font-medium ${storeData.is_active ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}
										>
											{storeData.is_active ? 'Activo' : 'Inactiva'}
										</span>
										{['admin'].includes(access) && (
											<Button
												className="!h-auto !w-fit flex-shrink-0 !rounded !px-3 !py-1.5 !text-sm !font-medium"
												variant="secondary"
												onClick={() => {
													setConfirmDialogProps({
														title: storeData.is_active ? 'Inactivar tienda' : 'Activar tienda',
														message: `¿Está seguro que desea ${storeData.is_active ? 'inactivar' : 'activar'} la tienda?`,
														onConfirm: async () => updateStoreField('is_active', storeData?.is_active ? false : true),
														onClose: () => setConfirmDialogProps(null)
													});
												}}
											>
												<span className="flex shrink-0 items-center gap-1.5">
													<Power className="size-4 shrink-0" />
													<span className="overflow-hidden text-ellipsis whitespace-nowrap">
														{storeData.is_active ? 'Desactivar' : 'Activar'}
													</span>
												</span>
											</Button>
										)}
									</div>
								</div>
							</div>

							{/* Other configuration items in a balanced grid */}
							<div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
								{/* Límite diario alcanzado */}
								<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
									<span className="text-sm text-neutral-400">Límite diario alcanzado</span>
									<div className="flex items-center gap-2">
										<span
											className={`rounded px-2 py-1 text-xs ${storeData.limit_reached ? 'bg-red-500/10 text-red-400' : 'bg-green-500/10 text-green-400'}`}
										>
											{storeData.limit_reached ? 'Sí' : 'No'}
										</span>
										{['admin', 'manager', 'postventa'].includes(access) && (
											<Button
												className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
												variant="secondary"
												onClick={() => {
													setConfirmDialogProps({
														title: storeData.limit_reached ? 'Deshabilitar límite diario' : 'Habilitar límite diario',
														message: `¿Está seguro que desea ${storeData.limit_reached ? 'deshabilitar' : 'habilitar'} el límite diario?`,
														onConfirm: async () => updateStoreField('limit_reached', storeData?.limit_reached ? false : true),
														onClose: () => setConfirmDialogProps(null)
													});
												}}
											>
												<span className="flex shrink-0 items-center gap-1">
													<Power className="size-3.5 shrink-0" />
													<span className="overflow-hidden text-ellipsis whitespace-nowrap"></span>
												</span>
											</Button>
										)}
									</div>
								</div>

								{/* Límite de ventas diarias */}
								<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
									<span className="text-sm text-neutral-400">Límite de ventas diarias</span>
									<div className="flex items-center gap-2">
										<span className={`text-sm ${storeData.limit_count !== null ? 'text-neutral-100' : 'text-neutral-500'}`}>
											{storeData.limit_count !== null ? storeData.limit_count : 'No establecido'}
										</span>
										{['admin', 'manager', 'postventa'].includes(access) && (
											<Button
												className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
												variant="secondary"
												onClick={() => {
													setEditDialogProps({
														title: 'Editar límite de ventas diarias',
														fields: [
															{
																name: 'limit_count',
																label: 'Límite de ventas diarias',
																type: 'input',
																placeholder: 'Ingrese el límite',
																allowedChars: 'numbers_only'
															}
														],
														onConfirm: async (values) => {
															const value = (values?.limit_count as string) || '';
															if (value === null || value === undefined || value === '') return 'Debe rellenar los campos.';
															else {
																return updateStoreField('limit_count', Math.abs(parseFloat(value)));
															}
														},
														onClose: () => setEditDialogProps(null)
													});
												}}
											>
												<span className="flex shrink-0 items-center gap-1">
													<Edit className="size-3.5 shrink-0" />
													<span className="overflow-hidden text-ellipsis whitespace-nowrap"></span>
												</span>
											</Button>
										)}
									</div>
								</div>

								{/* Límite de saldo */}
								<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
									<span className="text-sm text-neutral-400">Límite de saldo</span>
									<div className="flex items-center gap-2">
										<span className={`text-sm ${storeData.balance_threshold !== null ? 'text-neutral-100' : 'text-neutral-500'}`}>
											{storeData.balance_threshold !== null
												? `${currency_code}${storeData.balance_threshold.toFixed(2)}`
												: 'No establecido'}
										</span>
										{['admin', 'manager'].includes(access) && (
											<Button
												className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
												variant="secondary"
												onClick={() => {
													setEditDialogProps({
														title: 'Editar límite de saldo',
														fields: [
															{
																name: 'balance_threshold',
																label: 'Límite de saldo',
																type: 'input',
																placeholder: 'Ingrese el límite',
																allowedChars: 'numeric',
																defaultValue: storeData?.balance_threshold?.toString() || '35000'
															}
														],
														onConfirm: async (values) => {
															const value = (values?.balance_threshold as string) || '';
															if (value === null || value === undefined || value === '') return 'Debe rellenar los campos.';
															else {
																return updateStoreField('balance_threshold', Math.abs(parseFloat(value)) * -1);
															}
														},
														onClose: () => setEditDialogProps(null)
													});
												}}
											>
												<span className="flex shrink-0 items-center gap-1">
													<Edit className="size-3.5 shrink-0" />
													<span className="overflow-hidden text-ellipsis whitespace-nowrap"></span>
												</span>
											</Button>
										)}
									</div>
								</div>

								{/* Email de ventas diarias */}
								<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
									<span className="text-sm text-neutral-400">Email de ventas diarias</span>
									<div className="flex items-center gap-2">
										<span
											className={`rounded px-2 py-1 text-xs ${storeData.export_daily_sales ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-gray-400'}`}
										>
											{storeData.export_daily_sales ? 'Sí' : 'No'}
										</span>
										{['admin'].includes(access) && (
											<Button
												className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
												variant="secondary"
												onClick={() => {
													setConfirmDialogProps({
														title: storeData.export_daily_sales
															? 'Deshabilitar email de ventas diarias'
															: 'Habilitar email de ventas diarias',
														message: `¿Está seguro que desea ${storeData.export_daily_sales ? 'deshabilitar' : 'habilitar'} el email de ventas diarias?`,
														onConfirm: async () =>
															updateStoreField('export_daily_sales', storeData?.export_daily_sales ? false : true),
														onClose: () => setConfirmDialogProps(null)
													});
												}}
											>
												<span className="flex shrink-0 items-center gap-1">
													<Power className="size-3.5 shrink-0" />
													<span className="overflow-hidden text-ellipsis whitespace-nowrap"></span>
												</span>
											</Button>
										)}
									</div>
								</div>

								{/* Solo Tonic */}
								<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
									<span className="text-sm text-neutral-400">Solo Tonic</span>
									<div className="flex items-center gap-2">
										<span
											className={`rounded px-2 py-1 text-xs ${storeData.allow_only_tonic ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
										>
											{storeData.allow_only_tonic ? 'Sí' : 'No'}
										</span>
										{['admin'].includes(access) && (
											<Button
												className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
												variant="secondary"
												onClick={() => {
													setConfirmDialogProps({
														title: storeData.allow_only_tonic ? 'Desactivar solo Tonic' : 'Activar solo Tonic',
														message: `¿Está seguro que desea ${storeData.allow_only_tonic ? 'desactivar' : 'activar'} la opción de solo Tonic?`,
														onConfirm: async () =>
															updateStoreField('allow_only_tonic', storeData?.allow_only_tonic ? false : true),
														onClose: () => setConfirmDialogProps(null)
													});
												}}
											>
												<span className="flex shrink-0 items-center gap-1">
													<Power className="size-3.5 shrink-0" />
													<span className="overflow-hidden text-ellipsis whitespace-nowrap"></span>
												</span>
											</Button>
										)}
									</div>
								</div>

								{/* Laptops */}
								<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
									<span className="text-sm text-neutral-400">Laptops</span>
									<div className="flex items-center gap-2">
										<span
											className={`rounded px-2 py-1 text-xs ${storeData.allow_laptop ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
										>
											{storeData.allow_laptop ? 'Permitido' : 'No permitido'}
										</span>
										{['admin'].includes(access) && (
											<Button
												className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
												variant="secondary"
												onClick={() => {
													setConfirmDialogProps({
														title: storeData.allow_laptop ? 'Desactivar laptops' : 'Activar laptops',
														message: `¿Está seguro que desea ${storeData.allow_laptop ? 'desactivar' : 'activar'} la opción de laptops?`,
														onConfirm: async () => updateStoreField('allow_laptop', storeData?.allow_laptop ? false : true),
														onClose: () => setConfirmDialogProps(null)
													});
												}}
											>
												<span className="flex shrink-0 items-center gap-1">
													<Power className="size-3.5 shrink-0" />
													<span className="overflow-hidden text-ellipsis whitespace-nowrap"></span>
												</span>
											</Button>
										)}
									</div>
								</div>

								{/* Recargas en tienda */}
								<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
									<span className="text-sm text-neutral-400">Recargas en tienda</span>
									<div className="flex items-center gap-2">
										<span
											className={`rounded px-2 py-1 text-xs ${storeData.recarga_sales_access ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
										>
											{storeData.recarga_sales_access ? 'Activo' : 'Inactivo'}
										</span>
										{['admin'].includes(access) && (
											<Button
												className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
												variant="secondary"
												onClick={() => {
													setConfirmDialogProps({
														title: storeData.recarga_sales_access
															? 'Desactivar recargas en tienda'
															: 'Activar recargas en tienda',
														message: `¿Está seguro que desea ${storeData.recarga_sales_access ? 'desactivar' : 'activar'} las recargas en tienda?`,
														onConfirm: async () =>
															updateStoreField('recarga_sales_access', storeData?.recarga_sales_access ? false : true),
														onClose: () => setConfirmDialogProps(null)
													});
												}}
											>
												<span className="flex shrink-0 items-center gap-1">
													<Power className="size-3.5 shrink-0" />
													<span className="overflow-hidden text-ellipsis whitespace-nowrap"></span>
												</span>
											</Button>
										)}
									</div>
								</div>

								{/* Ventas de iPhones (Kandji) */}
								<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
									<span className="text-sm text-neutral-400">Ventas de iPhones (Kandji)</span>
									<div className="flex items-center gap-2">
										<span
											className={`rounded px-2 py-1 text-xs ${storeData.allow_iphone ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
										>
											{storeData.allow_iphone ? 'Activo' : 'Inactivo'}
										</span>
										{['admin'].includes(access) && (
											<Button
												className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
												variant="secondary"
												onClick={() => {
													setConfirmDialogProps({
														title: storeData.allow_iphone
															? 'Desactivar ventas de iPhones (Kandji)'
															: 'Activar ventas de iPhones (Kandji)',
														message: `¿Está seguro que desea ${storeData.allow_iphone ? 'desactivar' : 'activar'} las ventas de iPhones (Kandji)?`,
														onConfirm: async () =>
															updateStoreField('allow_iphone', storeData?.allow_iphone ? false : true),
														onClose: () => setConfirmDialogProps(null)
													});
												}}
											>
												<span className="flex shrink-0 items-center gap-1">
													<Power className="size-3.5 shrink-0" />
													<span className="overflow-hidden text-ellipsis whitespace-nowrap"></span>
												</span>
											</Button>
										)}
									</div>
								</div>

								{/* Portal de Cobranza */}
								<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
									<span className="text-sm text-neutral-400">Portal de Cobranza</span>
									<div className="flex items-center gap-2">
										<span
											className={`rounded px-2 py-1 text-xs ${storeData.cobranza_portal_access ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
										>
											{storeData.cobranza_portal_access ? 'Activo' : 'Inactivo'}
										</span>
										{['admin'].includes(access) && (
											<Button
												className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
												variant="secondary"
												onClick={() => {
													setConfirmDialogProps({
														title: storeData.cobranza_portal_access
															? 'Desactivar portal de cobranza'
															: 'Activar portal de cobranza',
														message: `¿Está seguro que desea ${storeData.cobranza_portal_access ? 'desactivar' : 'activar'} el portal de cobranza?`,
														onConfirm: async () =>
															updateStoreField('cobranza_portal_access', storeData?.cobranza_portal_access ? false : true),
														onClose: () => setConfirmDialogProps(null)
													});
												}}
											>
												<span className="flex shrink-0 items-center gap-1">
													<Power className="size-3.5 shrink-0" />
													<span className="overflow-hidden text-ellipsis whitespace-nowrap"></span>
												</span>
											</Button>
										)}
									</div>
								</div>

								{/* Portal de Promociones */}
								<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
									<span className="text-sm text-neutral-400">Portal de Promociones</span>
									<div className="flex items-center gap-2">
										<span
											className={`rounded px-2 py-1 text-xs ${storeData.promotions_portal_access ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
										>
											{storeData.promotions_portal_access ? 'Activo' : 'Inactivo'}
										</span>
										{['admin'].includes(access) && (
											<Button
												className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
												variant="secondary"
												onClick={() => {
													setConfirmDialogProps({
														title: storeData.promotions_portal_access
															? 'Desactivar portal de promociones'
															: 'Activar portal de promociones',
														message: `¿Está seguro que desea ${storeData.promotions_portal_access ? 'desactivar' : 'activar'} el portal de promociones?`,
														onConfirm: async () =>
															updateStoreField('promotions_portal_access', storeData?.promotions_portal_access ? false : true),
														onClose: () => setConfirmDialogProps(null)
													});
												}}
											>
												<span className="flex shrink-0 items-center gap-1">
													<Power className="size-3.5 shrink-0" />
													<span className="overflow-hidden text-ellipsis whitespace-nowrap"></span>
												</span>
											</Button>
										)}
									</div>
								</div>

								{/* Venta de Otros productos a Nivel Azul */}
								<div className="col-span-1 flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2 md:col-span-2 lg:col-span-2">
									<span className="text-sm text-neutral-400">Venta de Otros productos a Nivel Azul</span>
									<div className="flex items-center gap-2">
										<span
											className={`rounded px-2 py-1 text-xs ${storeData.allow_any_product ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
										>
											{storeData.allow_any_product ? 'Permitido' : 'No permitido'}
										</span>
										{['admin'].includes(access) && (
											<Button
												className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
												variant="secondary"
												onClick={() => {
													setConfirmDialogProps({
														title: storeData.allow_any_product
															? 'Desactivar venta de otros productos'
															: 'Activar venta de otros productos',
														message: `¿Está seguro que desea ${storeData.allow_any_product ? 'desactivar' : 'activar'} la venta de otros productos a nivel azul?`,
														onConfirm: async () =>
															updateStoreField('allow_any_product', storeData?.allow_any_product ? false : true),
														onClose: () => setConfirmDialogProps(null)
													});
												}}
											>
												<span className="flex shrink-0 items-center gap-1">
													<Power className="size-3.5 shrink-0" />
													<span className="overflow-hidden text-ellipsis whitespace-nowrap"></span>
												</span>
											</Button>
										)}
									</div>
								</div>
							</div>

							{/* Direcciones de reenvío */}
							<div className="rounded-md bg-neutral-800/30 p-3">
								<div className="mb-3 flex items-center justify-between">
									<span className="text-sm font-medium text-neutral-400">Direcciones email de reenvío</span>
									{['admin', 'manager', 'postventa'].includes(access) && (
										<Button
											className="!h-auto !w-fit !rounded !px-3 !py-1 !text-xs !font-normal"
											variant="secondary"
											onClick={() => {
												setEditDialogProps({
													title: 'Agregar email de reenvío',
													fields: [
														{
															name: 'forward_email_address',
															label: 'Email de reenvío',
															placeholder: 'email@ejemplo.com',
															type: 'input'
														}
													],
													onConfirm: async (values) => {
														const email = (values?.forward_email_address as string) || '';

														if (!email || !validate_email(email)) {
															return 'Por favor ingrese un email válido.';
														}
														const currentEmails = storeData.forward_email_address || [];
														if (currentEmails.includes(email)) {
															return 'Este email ya existe en la lista.';
														}
														const newEmails = [...currentEmails, email];
														return updateStoreField('forward_email_address', newEmails);
													},
													onClose: () => setEditDialogProps(null)
												});
											}}
										>
											<div className="flex items-center gap-1">
												<Plus className="size-3" />
												<span>Agregar</span>
											</div>
										</Button>
									)}
								</div>

								{storeData.forward_email_address && storeData.forward_email_address.length > 0 ? (
									<div className="space-y-2">
										{storeData.forward_email_address.map((email, index) => (
											<div key={index} className="flex items-center justify-between rounded-md bg-neutral-950/40 px-3 py-2">
												<div className="flex items-center gap-2">
													<Mail className="size-4 text-neutral-400" />
													<span className="text-sm text-neutral-100">{email}</span>
												</div>
												{['admin'].includes(access) && (
													<Button
														className="!h-auto !w-fit !rounded !px-2 !py-1 !text-xs !font-normal"
														variant="secondary"
														onClick={() => {
															setConfirmDialogProps({
																title: 'Eliminar email',
																message: `¿Está seguro que desea eliminar el email ${email}?`,
																onConfirm: async () => {
																	const newEmails = storeData.forward_email_address?.filter((_, i) => i !== index) || [];
																	return updateStoreField('forward_email_address', newEmails);
																},
																onClose: () => setConfirmDialogProps(null)
															});
														}}
													>
														<X className="size-3.5" />
													</Button>
												)}
											</div>
										))}
									</div>
								) : (
									<div className="py-2 text-center text-xs text-neutral-500">No hay direcciones configuradas</div>
								)}
							</div>
						</div>
					</div>

					<div className="mt-6 space-y-4">
						<div>
							<h2 className="text-2xl font-semibold text-neutral-100">Sucursales</h2>
							<p className="text-sm text-neutral-400">Tiendas y locales de venta</p>
						</div>

						{storeData.sucursales.length > 0 ? (
							<div className="mt-6 grid gap-3">
								{storeData.sucursales
									.map((sucursal, index) => {
										// Handle the case where sucursal might be an empty array
										if (Array.isArray(sucursal) && sucursal.length === 0) {
											return null;
										}

										// If it's an array with content, take the first item, otherwise use as is
										const sucursalData = Array.isArray(sucursal) ? sucursal[0] : sucursal;

										if (!sucursalData) return null;

										const hoursOfOperation = sucursalData.hours_of_operation;
										const isHoursString = typeof hoursOfOperation === 'string';

										return (
											<details key={index} className="group rounded-lg border border-neutral-800 bg-neutral-900/50">
												<summary className="cursor-pointer list-none p-6">
													<div className="no-scrollbar flex items-center justify-between gap-6 overflow-x-auto">
														<div className="flex shrink-0 items-center gap-2">
															<svg
																className="h-5 w-5 text-neutral-400 transition-transform group-open:rotate-90"
																fill="none"
																viewBox="0 0 24 24"
																stroke="currentColor"
															>
																<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
															</svg>
															<h3 className="text-xl font-semibold text-neutral-100">
																{sucursalData.store_name || `Sucursal ${index + 1}`}
															</h3>
															<span className="rounded bg-blue-500/10 px-2 py-1 text-xs text-blue-400 light:brightness-50">
																{sucursalData.city}
															</span>
														</div>
													</div>
												</summary>

												<div className="px-6 pb-6">
													<div className="space-y-4">
														<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
															<div className="space-y-1.5">
																<div className="flex items-start justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="shrink-0 text-sm text-neutral-400">Email</span>
																	<span className="break-words text-right text-sm text-neutral-100">
																		{sucursalData.store_email || 'No registrado'}
																	</span>
																</div>
																<div className="flex items-start justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="shrink-0 text-sm text-neutral-400">Ciudad</span>
																	<span className="break-words text-right text-sm text-neutral-100">
																		{sucursalData.city || 'No registrado'}
																	</span>
																</div>
																<div className="flex items-start justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="shrink-0 text-sm text-neutral-400">Ubicación</span>
																	<span className="ml-2 break-words text-right text-sm text-neutral-100">
																		{sucursalData.location || 'No registrado'}
																	</span>
																</div>
															</div>

															<div className="space-y-1.5">
																<div className="flex items-start justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="shrink-0 text-sm text-neutral-400">WhatsApp</span>
																	<span className="break-words text-right text-sm text-neutral-100">
																		{sucursalData.whatsapp || 'No registrado'}
																	</span>
																</div>
																<div className="flex items-start justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																	<span className="shrink-0 text-sm text-neutral-400">Instagram</span>
																	<span className="break-words text-right text-sm text-neutral-100">
																		{sucursalData.instagram || 'No registrado'}
																	</span>
																</div>
															</div>
														</div>

														{sucursalData.seller_accounts && sucursalData.seller_accounts.length > 0 && (
															<div className="mt-4">
																<h4 className="mb-2 text-lg font-medium text-neutral-100">Cuentas de vendedores</h4>
																<div className="space-y-2">
																	{sucursalData.seller_accounts.map((seller, sellerIndex) => (
																		<div key={sellerIndex} className="rounded-lg border border-neutral-800 bg-neutral-900/30 p-3">
																			<div className="grid grid-cols-1 gap-2 md:grid-cols-3">
																				<div className="flex items-center justify-between">
																					<span className="text-xs text-neutral-400">Nombre</span>
																					<span className="text-sm text-neutral-100">{seller.first_name}</span>
																				</div>
																				<div className="border-l-0 border-neutral-700 md:border-l md:pl-2">
																					<div className="flex items-center justify-between">
																						<span className="text-xs text-neutral-400">Apellido</span>
																						<span className="text-sm text-neutral-100">{seller.last_name}</span>
																					</div>
																				</div>
																				<div className="border-l-0 border-neutral-700 md:border-l md:pl-2">
																					<div className="flex items-center justify-between">
																						<span className="text-xs text-neutral-400">Email</span>
																						<span className="text-sm text-neutral-100">{seller.email}</span>
																					</div>
																				</div>
																			</div>
																		</div>
																	))}
																</div>
															</div>
														)}

														<div className="mt-4">
															<h4 className="mb-2 text-lg font-medium text-neutral-100">Horarios de operación</h4>
															{isHoursString ? (
																<div className="rounded-lg border border-neutral-800 bg-neutral-900/30 p-3">
																	<span className="text-sm text-neutral-100">{hoursOfOperation || 'No registrado'}</span>
																</div>
															) : (
																<div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
																	{Object.entries(hoursOfOperation).map(([day, hours]) => (
																		<div
																			key={day}
																			className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2"
																		>
																			<span className="text-sm text-neutral-400">{day}</span>
																			<div className="flex items-center gap-2">
																				<span
																					className={`rounded px-2 py-1 text-xs ${
																						hours.active ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
																					}`}
																				>
																					{hours.active ? `${hours.from}AM - ${hours.to}PM` : 'Cerrado'}
																				</span>
																			</div>
																		</div>
																	))}
																</div>
															)}
														</div>

														{(sucursalData.whatsapp_link || sucursalData.instagram_link) && (
															<div className="mt-4">
																<h4 className="mb-2 text-lg font-medium text-neutral-100">Enlaces</h4>
																<div className="space-y-2">
																	{sucursalData.whatsapp_link && (
																		<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																			<span className="text-sm text-neutral-400">WhatsApp Link</span>
																			<a
																				href={sucursalData.whatsapp_link}
																				target="_blank"
																				rel="noopener noreferrer"
																				className="text-sm text-blue-400 hover:text-blue-300"
																			>
																				Abrir enlace
																			</a>
																		</div>
																	)}
																	{sucursalData.instagram_link && (
																		<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
																			<span className="text-sm text-neutral-400">Instagram Link</span>
																			<a
																				href={sucursalData.instagram_link}
																				target="_blank"
																				rel="noopener noreferrer"
																				className="text-sm text-blue-400 hover:text-blue-300"
																			>
																				Abrir enlace
																			</a>
																		</div>
																	)}
																</div>
															</div>
														)}
													</div>
												</div>
											</details>
										);
									})
									.filter(Boolean)}
							</div>
						) : (
							<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6 text-center text-sm text-neutral-300">
								No se encontraron sucursales
							</div>
						)}
					</div>

					<div className="mt-6 space-y-4">
						<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
							<div>
								<h2 className="text-2xl font-semibold text-neutral-100">Información bancaria</h2>
								<p className="text-sm text-neutral-400">Datos de la cuenta bancaria</p>
							</div>
							{['admin'].includes(access) && (
								<Button
									className="!h-auto !w-fit !rounded !px-2 !py-1.5 !text-xs !font-normal sm:!px-3 sm:!py-2 sm:!text-sm"
									variant="secondary"
									onClick={() => {
										setEditDialogProps({
											title: 'Editar información bancaria',
											fields: [
												{
													name: 'bank',
													label: 'Banco',
													type: 'select',
													options: banks.map((bank) => ({ value: bank?.toUpperCase(), label: bank?.toUpperCase() })),
													defaultValue: storeData.bank_name?.toUpperCase() || ''
												},
												{
													name: 'bank_account_holder',
													label: 'Titular de la cuenta',
													type: 'input',
													placeholder: 'Nombre del titular',
													defaultValue: storeData.account_name || ''
												},
												{
													name: 'bank_account_type',
													label: 'Tipo de cuenta',
													type: 'select',
													options: [
														{ value: 'corriente', label: 'Corriente' },
														{ value: 'ahorro', label: 'Ahorro' },
														...(internalCountry.get() === 'bolivia' ? [{ value: 'yape', label: 'Billetera' }] : [])
													],
													defaultValue: storeData.account_type?.toLowerCase() || ''
												},
												{
													name: 'bank_account_number',
													label: 'Número de cuenta',
													type: 'input',
													placeholder: 'Número de cuenta bancaria',
													defaultValue: storeData.account_number || ''
												},
												{
													name: 'bank_identification_type',
													label: 'Tipo de documento',
													type: 'select',
													options:
														internalCountry.get() === 'venezuela'
															? ['RIF C.A. (J-)', 'Cédula Natural (V-)', 'RIF F.P. (V-)', 'Cédula Extranjera'].map((e, i) => ({
																	label: e,
																	value: i === 0 ? 'rif' : i === 1 ? 'cedula' : i === 2 ? 'firma_personal' : 'extranjera'
																}))
															: internalCountry.get() === 'rd'
																? ['Cédula', 'RNC', 'Pasaporte'].map((e, i) => ({
																		label: e,
																		value: i === 0 ? 'cedula' : i === 1 ? 'rnc' : 'passport'
																	}))
																: internalCountry.get() === 'colombia'
																	? ['Cédula'].map((e, i) => ({ label: e, value: 'cedula' }))
																	: internalCountry.get() === 'bolivia'
																		? ['Cédula', 'NIT'].map((e, i) => ({ label: e, value: i === 0 ? 'cedula' : 'nit' }))
																		: ['DNI', 'Ficha RUC'].map((e, i) => ({ label: e, value: i === 0 ? 'dni' : 'ruc' })),
													defaultValue: storeData.document_type?.toLowerCase() || ''
												},
												{
													name: 'bank_identification_number',
													label: 'Número de identificación',
													type: 'input',
													placeholder: 'Número de identificación',
													defaultValue: storeData.identification_number || ''
												}
											],
											onConfirm: async (values) => {
												const bank = (values?.bank as string)?.trim();
												const bank_account_holder = (values?.bank_account_holder as string)?.trim();
												const bank_account_type = values?.bank_account_type as 'corriente' | 'ahorro';
												const bank_account_number = (values?.bank_account_number as string)?.trim();
												const bank_identification_type = (values?.bank_identification_type as string)?.trim();
												const bank_identification_number = (values?.bank_identification_number as string)?.trim();

												if (!bank || !bank_account_holder || !bank_account_type || !bank_account_number) {
													return 'Todos los campos bancarios son requeridos.';
												}

												// Validate bank account number format
												if (!validate_bank_account_number(bank_account_number, internalCountry.get(), bank)) {
													return 'El número de cuenta bancaria no tiene un formato válido.';
												}

												// Validate identification number
												if (bank_identification_type && bank_identification_number) {
													if (
														!validate_identity_number(
															bank_identification_number,
															bank_identification_type as IdentityType,
															internalCountry.get()
														)
													) {
														return 'El número de identificación no tiene un formato válido.';
													}
												}

												try {
													const response = (await unsafeAPI(
														'PUT',
														'https://api.krece.app/storeprofile/masteruser/',
														{
															email: storeData.store_email?.toLowerCase()?.trim(),
															bank,
															bank_account_holder,
															bank_account_type,
															bank_account_number,
															...(bank_identification_type && { bank_identification_type }),
															...(bank_identification_number && { bank_identification_number })
														},
														userData,
														internalCountry.get()
													)) as any;

													if (response?.success === true) {
														setDialogMessage('Información bancaria actualizada exitosamente.');
														// Refetch store data to get updated information
														await searchStoreDataForEmail(selectedEmail);
													} else {
														return response?.status?.length ? response.status : 'Ocurrió un problema, intente más tarde.';
													}
												} catch (err) {
													return 'Error: ' + (err as Error).message;
												}

												return true;
											},
											onClose: () => setEditDialogProps(null)
										});
									}}
								>
									<span className="flex shrink-0 items-center gap-1.5 sm:gap-2">
										<Edit className="size-3.5 shrink-0 sm:size-4" />
										<span className="xs:inline hidden sm:inline">Editar información bancaria</span>
										<span className="xs:hidden sm:hidden">Editar banco</span>
									</span>
								</Button>
							)}
						</div>

						<div className="grid grid-cols-1 gap-3 rounded-lg border border-neutral-800 bg-neutral-900/50 p-3 md:grid-cols-2">
							<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
								<span className="text-sm text-neutral-400">Nombre de la cuenta</span>
								<span className="text-sm text-neutral-100">{storeData.account_name || 'No registrado'}</span>
							</div>

							<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
								<span className="text-sm text-neutral-400">Número de cuenta</span>
								<span className="text-sm text-neutral-100">{storeData.account_number || 'No registrado'}</span>
							</div>

							<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
								<span className="text-sm text-neutral-400">Tipo de cuenta</span>
								<span className="text-sm text-neutral-100">{storeData.account_type || 'No registrado'}</span>
							</div>

							<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
								<span className="text-sm text-neutral-400">Banco</span>
								<span className="text-sm text-neutral-100">{storeData.bank_name || 'No registrado'}</span>
							</div>

							<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
								<span className="text-sm text-neutral-400">Tipo de documento</span>
								<span className="text-sm text-neutral-100">{storeData.document_type || 'No registrado'}</span>
							</div>

							<div className="flex items-center justify-between rounded-md bg-neutral-800/30 px-3 py-2">
								<span className="text-sm text-neutral-400">Número de identificación</span>
								<span className="text-sm text-neutral-100">{storeData.identification_number || 'No registrado'}</span>
							</div>
						</div>
					</div>
				</>
			)}

			{storeData === null && !loading && (
				<div className="mt-6 rounded-lg border border-neutral-800 bg-neutral-900/50 p-6 text-center text-sm text-neutral-300">
					No se encontró información
				</div>
			)}
		</div>
	);
}

interface StoreSearchOption {
	label: string;
	value: string;
	description?: string;
	searchText?: string;
}

interface HoursOfOperation {
	active: boolean;
	from: number;
	to: number;
}

interface SellerAccount {
	first_name: string;
	email: string;
	last_name: string;
}

interface Sucursal {
	location: string;
	store_email: string;
	logo: string | null;
	seller_accounts?: SellerAccount[];
	city: string;
	instagram_link: string | null;
	hours_of_operation:
		| {
				Lunes: HoursOfOperation;
				Martes: HoursOfOperation;
				Miércoles: HoursOfOperation;
				Jueves: HoursOfOperation;
				Viernes: HoursOfOperation;
				Sábado: HoursOfOperation;
				Domingo: HoursOfOperation;
		  }
		| string;
	whatsapp: string | null;
	whatsapp_link: string | null;
	store_name: string;
	instagram: string | null;
}

type AdminEliteProgram = {
	status: EliteStoreStatus;
	benefits_active: boolean;
	program_enabled: boolean;
	membership_current: boolean;
	qualification_type: EliteQualificationType | null;
	restricted_branches: Array<{ branch: string }>;
};

function getEliteLabel(elite?: AdminEliteProgram) {
	if (!elite) return 'Información no disponible';
	if (!elite.program_enabled) return 'Programa desactivado en este país';
	if (!elite.membership_current) return 'Sin evaluación vigente';
	const label = elite.qualification_type === 'TOP_SALES' ? 'Élite Top Ventas' : 'Élite';
	if (elite.status === 'ELITE_STABLE') return label;
	if (elite.status === 'ELITE_AT_RISK') return `${label} en riesgo`;
	if (elite.status === 'NEAR_ELITE') return 'Cerca de ser Élite';
	if (elite.status === 'NOT_ELIGIBLE') return 'No elegible';
	if (elite.status === 'STANDARD') return 'No Élite';
	return 'Información no disponible';
}

interface StoreData {
	elite_program?: AdminEliteProgram;
	store_email: string;
	store_name: string;
	store_phone: string;
	store_zip: string;
	is_active: boolean;
	created_at: string | null;
	account_name: string;
	account_number: string;
	account_type: string;
	bank_name: string;
	sucursales: (Sucursal[] | [])[];
	allow_only_tonic: boolean;
	allow_any_product: boolean;
	allow_laptop: boolean;
	recarga_sales_access: boolean;
	allow_iphone: boolean;
	cobranza_portal_access: boolean;
	promotions_portal_access: boolean;
	balance: number | null;
	forward_email_address: string[];
	balance_threshold: number;
	export_daily_sales: boolean;
	limit_reached: boolean;
	limit_count: number;
	document_type: string;
	identification_number: string;
}
